import React, { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { login02, loginlogo } from "../../imagepath";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import config from "../../../config";
import { useForm } from "react-hook-form";
import Select from "react-select";
import axios from "axios";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTrash } from "@fortawesome/free-solid-svg-icons";

const RegisterBusiness = () => {
  const { state } = useLocation();
  const navigate = useNavigate();
  const {
    register,
    handleSubmit,
    watch,
    setValue,
    setError,
    clearErrors,
    getValues,
    formState: { errors },
  } = useForm();

  const [loading, setLoading] = useState(false);
  const [phoneNumber, setPhoneNumber] = useState(state?.phone || "");
  const [phoneError, setPhoneError] = useState("");
  const [additionalPhones, setAdditionalPhones] = useState([]);
  const [additionalPhoneErrors, setAdditionalPhoneErrors] = useState([]);
  const [additionalEmails, setAdditionalEmails] = useState([]);
  const [additionalEmailErrors, setAdditionalEmailErrors] = useState([]);
  const [specialization, setSpecialization] = useState([]);
  const [institutionType, setInstitutionType] = useState(null);
  const [emailError, setEmailError] = useState("");
  const [instituteTypeOptions, setInstituteTypeOptions] = useState([]);
  const [selectedInstitute, setSelectedInstitute] = useState(null);
  const [specializationOptionsUpdated, setSpecializationOptionsUpdated] = useState([]);
  const [vanityUrl, setVanityUrl] = useState("");
  const [vanityStatus, setVanityStatus] = useState(null);

  // Fetch institute types
  useEffect(() => {
    axios
      .get(`${config.API_URL}get_institutes.php`)
      .then((response) => {
        if (Array.isArray(response.data.institutes)) {
          const options = response.data.institutes.map((name) => ({
            value: name,
            label: name,
          }));
          setInstituteTypeOptions(options);
        }
      })
      .catch((error) => console.error("Error fetching institutes:", error));
  }, []);

  // Fetch specializations based on selected institute
  useEffect(() => {
    if (selectedInstitute) {
      axios
        .get(`${config.API_URL}get_specializations.php?institute=${selectedInstitute.value}`)
        .then((response) => {
          if (Array.isArray(response.data.specializations)) {
            const options = response.data.specializations.map((spec) => ({
              value: spec,
              label: spec,
            }));
            setSpecializationOptionsUpdated(options);
          }
        })
        .catch((error) => console.error("Error fetching specializations:", error));
    }
  }, [selectedInstitute]);

  // Vanity URL generation
  useEffect(() => {
    const subscription = watch((value, { name }) => {
      if (name === "institution_name") {
        const generatedVanity = value.institution_name
          ?.toLowerCase()
          .replace(/\s+/g, "-")
          .replace(/[^a-z0-9-]/g, "")
          .replace(/-+/g, "-");
        setValue("vanity_url", generatedVanity);
        setVanityUrl(generatedVanity);
      }
    });
    return () => subscription.unsubscribe();
  }, [watch, setValue]);

  // Check vanity URL availability
  useEffect(() => {
    if (!vanityUrl) return;
    const delayDebounceFn = setTimeout(() => {
      checkVanityUrlAvailability(vanityUrl);
    }, 500);
    return () => clearTimeout(delayDebounceFn);
  }, [vanityUrl]);

  const checkVanityUrlAvailability = async (url) => {
    setLoading(true);
    try {
      const response = await fetch(`${config.API_URL}check_vanity_url.php`, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: new URLSearchParams({ vanity_url: url }).toString(),
      });
      const data = await response.json();
      setVanityStatus(data.status === "taken" ? "taken" : "available");
    } catch (error) {
      console.error("Error checking vanity URL:", error);
      setVanityStatus(null);
    }
    setLoading(false);
  };

  // Fetch city and state based on pin code
  const fetchCityAndState = async (pinCode) => {
    if (pinCode.length !== 6) return;
    try {
      const response = await fetch(`https://api.postalpincode.in/pincode/${pinCode}`);
      const data = await response.json();
      if (data && data[0]?.Status === "Success" && data[0]?.PostOffice?.length) {
        const { District, State } = data[0].PostOffice[0];
        setValue("city", District);
        setValue("state", State);
        clearErrors("pin_code");
      } else {
        setValue("city", "");
        setValue("state", "");
        setError("pin_code", { type: "manual", message: "Invalid Pin Code" });
      }
    } catch (error) {
      setError("pin_code", { type: "manual", message: "Error fetching data" });
    }
  };

  const handleSelectChange = (selectedOption) => {
    if (!selectedOption) {
      setInstitutionType("");
      setSpecialization([]);
      setSpecializationOptionsUpdated([]);
    } else {
      setInstitutionType(selectedOption.value);
      setSpecialization([]);
    }
    setSelectedInstitute(selectedOption);
  };

  const handleSpecializationsChange = (selectedOptions) => {
    setSpecialization(selectedOptions);
  };

  const handlePinCodeChange = (e) => {
    let pinCode = e.target.value.replace(/\D/g, "");
    if (pinCode.length > 6) {
      setError("pin_code", { type: "manual", message: "Pin code should not exceed 6 digits" });
      return;
    } else if (pinCode.length < 6 && pinCode.length > 0) {
      setError("pin_code", { type: "manual", message: "Pin code must be 6 digits" });
    } else {
      clearErrors("pin_code");
    }
    setValue("pin_code", pinCode.slice(0, 6));
    if (pinCode.length === 6) fetchCityAndState(pinCode);
    else {
      setValue("city", "");
      setValue("state", "");
    }
  };

  const validatePhoneNumber = (phoneNumber, countryCode) => {
    const phoneLength = phoneNumber.length;
    switch (countryCode) {
      case "in":
      case "gb":
      case "us":
      case "bd":
        return phoneLength === 10;
      case "ae":
      case "lk":
        return phoneLength === 9;
      case "ng":
        return phoneLength === 11;
      default:
        return false;
    }
  };

  const handlePhoneNumberChange = (value, country) => {
    setPhoneNumber(value);
    const formattedPhone = value.replace(country.dialCode, "");
    const isValid = validatePhoneNumber(formattedPhone, country.countryCode);
    if (!isValid && value) {
      setPhoneError(`Invalid phone number length for ${country.name}`);
    } else {
      setPhoneError("");
    }
  };

  const handleAdditionalPhoneChange = (index, value, country) => {
    const newPhones = [...additionalPhones];
    newPhones[index] = value;
    setAdditionalPhones(newPhones);
    const formattedPhone = value.replace(/\D/g, "").replace(country.dialCode, "");
    const isValid = validatePhoneNumber(formattedPhone, country.countryCode);
    const newErrors = [...additionalPhoneErrors];
    if (!isValid && value) {
      newErrors[index] = `Invalid phone number length for ${country.name}`;
    } else {
      newErrors[index] = "";
    }
    setAdditionalPhoneErrors(newErrors);
  };

  const addPhone = () => {
    setAdditionalPhones([...additionalPhones, ""]);
    setAdditionalPhoneErrors([...additionalPhoneErrors, ""]);
  };

  const removePhone = (index) => {
    const newPhones = [...additionalPhones];
    newPhones.splice(index, 1);
    setAdditionalPhones(newPhones);
    const newErrors = [...additionalPhoneErrors];
    newErrors.splice(index, 1);
    setAdditionalPhoneErrors(newErrors);
  };

  const handleAdditionalEmailChange = (index, value) => {
    const newEmails = [...additionalEmails];
    newEmails[index] = value;
    setAdditionalEmails(newEmails);
    const newErrors = [...additionalEmailErrors];
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(value) && value.length > 0) {
      newErrors[index] = "Please enter a valid email address.";
    } else {
      newErrors[index] = "";
    }
    setAdditionalEmailErrors(newErrors);
  };

  const addEmail = () => {
    setAdditionalEmails([...additionalEmails, ""]);
    setAdditionalEmailErrors([...additionalEmailErrors, ""]);
  };

  const removeEmail = (index) => {
    const newEmails = [...additionalEmails];
    newEmails.splice(index, 1);
    setAdditionalEmails(newEmails);
    const newErrors = [...additionalEmailErrors];
    newErrors.splice(index, 1);
    setAdditionalEmailErrors(newErrors);
  };

  const handleEmailChange = (e) => {
    const emailValue = e.target.value;
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(emailValue) && emailValue.length > 0) {
      setEmailError("Please enter a valid email address.");
    } else {
      setEmailError("");
    }
    setValue("email", emailValue);
  };

  const onSubmit = async (data) => {
    if (vanityStatus !== "available") {
      toast.error("Please choose an available vanity URL.");
      return;
    }
    setLoading(true);

    try {
      const formattedSpecializations = specialization.map((spec) => spec.value).join(",");
      const formattedAdditionalPhones = additionalPhones.filter((phone) => phone).join(",");
      const formattedAdditionalEmails = additionalEmails.filter((email) => email).join(",");

      const payload = {
        user_id: state?.user_id || localStorage.getItem("userUUID") || "",
        mobile_number: phoneNumber,
        additional_phones: formattedAdditionalPhones,
        email: data.email || "",
        additional_emails: formattedAdditionalEmails,
        institution_name: data.institution_name,
        institute_type: institutionType,
        specializations: formattedSpecializations,
        address_line1: data.address_line1,
        address_line2: data.address_line2,
        city: data.city,
        state: data.state,
        pin_code: data.pin_code,
        vanity_url: vanityUrl,
      };

      const response = await fetch(`${config.API_URL}register_business.php`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      const responseData = await response.json();
      console.log("API Response:", responseData); // Debug log

      if (responseData.status === "success") {
        localStorage.setItem("businessId", responseData.business_id);
        toast.success(responseData.message);
        navigate("/admin-dashboard", { replace: true }); // Navigate immediately
      } else {
        toast.error(responseData.message || "Registration failed. Please try again.");
      }
    } catch (error) {
      console.error("Error during registration:", error);
      toast.error("An error occurred. Please try again later.");
    } finally {
      setLoading(false);
    }
  };

  const allFieldsFilled = () => {
    const values = getValues();
    const emailValue = values.email || "";
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const isEmailValid = emailValue ? emailRegex.test(emailValue) : true;

    return (
      (state?.user_id || localStorage.getItem("userUUID")) &&
      institutionType &&
      specialization.length > 0 &&
      values.institution_name &&
      vanityUrl &&
      vanityStatus === "available" &&
      values.address_line1 &&
      values.pin_code?.length === 6 &&
      values.city &&
      values.state &&
      isEmailValid
    );
  };

  return (
    <>
      <ToastContainer />
      <div className="main-wrapper login-body">
        <div className="container-fluid px-0">
          <div className="row">
            <div className="col-lg-6 login-wrap">
              <div className="login-sec">
                <div className="log-img">
                  <img className="img-fluid" src={login02} alt="Logo" />
                </div>
              </div>
            </div>
            <div className="col-lg-6 login-wrap-bg">
              <div className="login-wrapper">
                <div className="loginbox">
                  <div className="login-right">
                    <div className="login-right-wrap">
                      <div className="account-logo">
                        <Link to="#">
                          <img src={loginlogo} height={60} alt="#" />
                        </Link>
                      </div>
                      <h2>Getting Started</h2>
                      <form onSubmit={handleSubmit(onSubmit)}>
                        <div className="form-group" style={{ zIndex: 9999 }}>
                          <Select
                            options={instituteTypeOptions}
                            placeholder="Select Institute Type"
                            onChange={handleSelectChange}
                            isClearable
                          />
                          <label>
                            Institute Type <span className="login-danger">*</span>
                          </label>
                        </div>
                        {institutionType && (
                          <div className="form-group" style={{ zIndex: 999 }}>
                            <Select
                              required
                              onChange={handleSpecializationsChange}
                              isMulti
                              options={specializationOptionsUpdated}
                              value={specialization}
                              placeholder="Select Specialization"
                            />
                            <label>
                              Specialization <span className="login-danger">*</span>
                            </label>
                          </div>
                        )}
                        {specialization.length > 0 && (
                          <>
                            <div className="row">
                              <div className="col-md-6 form-group">
                                <label>
                                  Institution Name <span className="login-danger">*</span>
                                </label>
                                <input
                                  required
                                  type="text"
                                  {...register("institution_name")}
                                  className="form-control"
                                  placeholder="Institution Name"
                                />
                              </div>
                              <div className="col-md-6 form-group">
                                <label>
                                  Vanity URL <span className="login-danger">*</span>
                                </label>
                                <input
                                  required
                                  type="text"
                                  {...register("vanity_url", {
                                    required: "Vanity URL is required",
                                    pattern: {
                                      value: /^[a-z0-9-]+$/,
                                      message: "Only lowercase letters, numbers, and hyphens allowed",
                                    },
                                  })}
                                  value={vanityUrl}
                                  onChange={(e) => {
                                    const newValue = e.target.value
                                      .toLowerCase()
                                      .replace(/\s+/g, "-")
                                      .replace(/[^a-z0-9-]/g, "");
                                    setVanityUrl(newValue);
                                    setValue("vanity_url", newValue);
                                  }}
                                  className={`form-control ${
                                    vanityUrl
                                      ? vanityStatus === "taken"
                                        ? "is-invalid"
                                        : vanityStatus === "available"
                                        ? "is-valid"
                                        : ""
                                      : ""
                                  }`}
                                  placeholder="Vanity URL"
                                />
                                {vanityUrl && vanityStatus === "taken" && (
                                  <span className="text-danger">This vanity URL is already taken.</span>
                                )}
                                {vanityUrl && vanityStatus === "available" && (
                                  <span className="text-success">This vanity URL is available!</span>
                                )}
                              </div>
                            </div>
                            <div className="form-group">
                              <label>
                                Address Line 1 <span className="login-danger">*</span>
                              </label>
                              <input
                                required
                                type="text"
                                {...register("address_line1")}
                                className="form-control"
                                placeholder="Address Line 1"
                              />
                            </div>
                            <div className="form-group">
                              <label>Address Line 2</label>
                              <input
                                type="text"
                                {...register("address_line2")}
                                className="form-control"
                                placeholder="Address Line 2"
                              />
                            </div>
                            <div className="row">
                              <div className="col-md-4 form-group">
                                <label>
                                  Pin Code <span className="login-danger">*</span>
                                </label>
                                <input
                                  required
                                  type="text"
                                  value={getValues("pin_code") || ""}
                                  onChange={handlePinCodeChange}
                                  className="form-control"
                                  placeholder="Pin Code"
                                />
                                {errors.pin_code && (
                                  <span className="text-danger">{errors.pin_code.message}</span>
                                )}
                              </div>
                              <div className="col-md-4 form-group">
                                <label>
                                  City <span className="login-danger">*</span>
                                </label>
                                <input
                                  type="text"
                                  {...register("city")}
                                  className="form-control"
                                  placeholder="City"
                                  readOnly
                                />
                              </div>
                              <div className="col-md-4 form-group">
                                <label>
                                  State <span className="login-danger">*</span>
                                </label>
                                <input
                                  type="text"
                                  {...register("state")}
                                  className="form-control"
                                  placeholder="State"
                                  readOnly
                                />
                              </div>
                            </div>
                            <div className="form-group">
                        
                              <PhoneInput
                                onlyCountries={["in", "gb", "us", "ae", "ng", "lk", "bd"]}
                                country="in"
                                value={phoneNumber}
                                onChange={handlePhoneNumberChange}
                                className=" phoneinput"
                                inputProps={{ required: false }}
                                countryCodeEditable={false}
                              />
                               <label>Phone Number</label>
                              {phoneError && <span className="text-danger">{phoneError}</span>}
                            </div>
                            <div className="form-group">
                              <label>Email</label>
                              <input
                                type="email"
                                {...register("email")}
                                className="form-control"
                                placeholder="Business Email"
                                onChange={handleEmailChange}
                              />
                              {emailError && <span className="text-danger">{emailError}</span>}
                            </div>
                            <div className="form-group">
                              <p
                                style={{ cursor: "pointer", color: "blue", textDecoration: "underline" }}
                                onClick={addPhone}
                              >
                                Add Phone (Optional)
                              </p>
                              {additionalPhones.map((phone, index) => (
                                <div key={index} className="d-flex align-items-center mb-2">
                                  <PhoneInput
                                    country="in"
                                    value={phone}
                                    onChange={(value, country) =>
                                      handleAdditionalPhoneChange(index, value, country)
                                    }
                                    className="form-control phoneinput"
                                    placeholder="Additional Phone"
                                    countryCodeEditable={false}
                                    onlyCountries={["in", "gb", "us", "ae", "ng", "lk", "bd"]}
                                  />
                                  <button
                                    type="button"
                                    className="btn btn-danger ml-2"
                                    onClick={() => removePhone(index)}
                                  >
                                    <FontAwesomeIcon icon={faTrash} />
                                  </button>
                                  {additionalPhoneErrors[index] && (
                                    <span className="text-danger">{additionalPhoneErrors[index]}</span>
                                  )}
                                </div>
                              ))}
                            </div>
                            <div className="form-group">
                              <p
                                style={{ cursor: "pointer", color: "blue", textDecoration: "underline" }}
                                onClick={addEmail}
                              >
                                Add Email (Optional)
                              </p>
                              {additionalEmails.map((email, index) => (
                                <div key={index} className="d-flex align-items-center mb-2">
                                  <input
                                    type="email"
                                    value={email}
                                    onChange={(e) => handleAdditionalEmailChange(index, e.target.value)}
                                    className="form-control"
                                    placeholder="Additional Email"
                                  />
                                  <button
                                    type="button"
                                    className="btn btn-danger ml-2"
                                    onClick={() => removeEmail(index)}
                                  >
                                    <FontAwesomeIcon icon={faTrash} />
                                  </button>
                                  {additionalEmailErrors[index] && (
                                    <span className="text-danger">{additionalEmailErrors[index]}</span>
                                  )}
                                </div>
                              ))}
                            </div>
                            <div className="form-group login-btn">
                              <button
                                className="btn btn-primary btn-block"
                                type="submit"
                                disabled={loading || !allFieldsFilled()}
                              >
                                {loading ? "Registering..." : "Sign up"}
                              </button>
                            </div>
                          </>
                        )}
                      </form>
                      <div className="next-sign">
                        {/* <p className="account-subtitle">
                          Already have an account? <Link to="/login">Login</Link>
                        </p> */}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default RegisterBusiness;